import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/App/store'

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

/**
 * Serializable subset of the Firebase User object.
 * Raw Firebase User has methods and circular refs that break Redux.
 */
export interface SerializedFirebaseUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  phoneNumber: string | null
  providerId: string
  emailVerified: boolean
}

export interface AppUser {
  id?: string
  firstName?: string
  lastName?: string
  email?: string
  mobileNumber?: string
  [key: string]: unknown
}

export type AuthStage = 'idle' | 'needs_profile' | 'oauth_needs_phone' | 'authenticated'

interface AuthState {
  user: SerializedFirebaseUser | null
  appUser: AppUser | null
  idToken: string | null
  loading: boolean
  initializing: boolean
  error: string | null
  stage: AuthStage
  firstName: string
  lastName: string
}

// ═══════════════════════════════════════════════════════════════
// Initial State
// ═══════════════════════════════════════════════════════════════

const initialState: AuthState = {
  user: null,
  appUser: null,
  idToken: null,
  loading: false,
  initializing: true,
  error: null,
  stage: 'idle',
  firstName: '',
  lastName: '',
}

// ═══════════════════════════════════════════════════════════════
// Slice
// ═══════════════════════════════════════════════════════════════

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    initComplete(
      state,
      action: PayloadAction<{
        user: SerializedFirebaseUser | null
        appUser: AppUser | null
        idToken: string | null
      }>
    ) {
      if (action.payload.user) {
        state.user = action.payload.user
        if (action.payload.appUser) {
          state.appUser = action.payload.appUser
          state.idToken = action.payload.idToken
          state.stage = 'authenticated'
        } else if (state.appUser && state.idToken) {
          state.stage = 'authenticated'
        } else {
          // User exists but no app user - keep current stage if it's oauth_needs_phone
          // This handles the case where OAuth redirect completes but user hasn't added phone yet
          state.idToken = action.payload.idToken
          if (state.stage !== 'oauth_needs_phone') {
            state.stage = 'idle'
          }
        }
      } else if (state.appUser && state.idToken) {
        state.stage = 'authenticated'
      } else {
        state.user = null
        state.appUser = null
        state.idToken = null
        state.stage = 'idle'
      }
      state.initializing = false
      state.loading = false
    },

    oauthStart(state) {
      state.loading = true
      state.error = null
    },

    oauthExistingUser(
      state,
      action: PayloadAction<{
        user: SerializedFirebaseUser
        appUser: AppUser
        idToken: string
      }>
    ) {
      state.user = action.payload.user
      state.appUser = action.payload.appUser
      state.idToken = action.payload.idToken
      state.loading = false
      state.initializing = false
      state.stage = 'authenticated'
    },

    oauthNewUser(
      state,
      action: PayloadAction<{
        user: SerializedFirebaseUser
        idToken: string
        firstName: string
        lastName: string
      }>
    ) {
      state.user = action.payload.user
      state.idToken = action.payload.idToken
      state.firstName = action.payload.firstName
      state.lastName = action.payload.lastName
      state.loading = false
      state.initializing = false
      state.stage = 'oauth_needs_phone'
    },

    setAppUser(state, action: PayloadAction<AppUser>) {
      state.appUser = action.payload
      state.stage = 'authenticated'
      state.loading = false
    },

    mobileAuthComplete(
      state,
      action: PayloadAction<{ appUser: AppUser; idToken: string }>
    ) {
      state.appUser = action.payload.appUser
      state.idToken = action.payload.idToken
      state.stage = 'authenticated'
      state.loading = false
      state.initializing = false
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },

    setError(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.loading = false
    },

    signedOut() {
      return { ...initialState, initializing: false }
    },

    clearError(state) {
      state.error = null
    },
  },
})

// ═══════════════════════════════════════════════════════════════
// Exports
// ═══════════════════════════════════════════════════════════════

export const authActions = authSlice.actions

// ─── Selectors ───────────────────────────────────────────────

export const selectAuth = (state: RootState) => state.auth
export const selectIsAuthenticated = (state: RootState) =>
  !!state.auth.user || !!state.auth.appUser
export const selectAuthUser = (state: RootState) => state.auth.user
export const selectAppUser = (state: RootState) => state.auth.appUser
export const selectIdToken = (state: RootState) => state.auth.idToken
export const selectAuthLoading = (state: RootState) =>
  state.auth.loading || state.auth.initializing
export const selectAuthInitializing = (state: RootState) =>
  state.auth.initializing
export const selectAuthStage = (state: RootState) => state.auth.stage
export const selectAuthError = (state: RootState) => state.auth.error
export const selectAuthFirstName = (state: RootState) => state.auth.firstName
export const selectAuthLastName = (state: RootState) => state.auth.lastName

export default authSlice.reducer
